import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

/* ── helpers ─────────────────────────────────────────── */
const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString());
const fmtUsd = (n) => {
  if (n == null) return '-';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Number(n).toFixed(2)}`;
};
const fmtPct = (n) => (n == null ? '-' : `${Number(n).toFixed(1)}%`);
const ago = (d) => {
  if (!d) return '-';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/* ── tiny components ─────────────────────────────────── */
function Stat({ label, value, sub, color = 'blue' }) {
  const styles = {
    blue:   'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50',
    green:  'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700/50',
    yellow: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-700/50',
    red:    'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700/50',
    purple: 'bg-purple-50 border-purple-200 dark:bg-purple-900/30 dark:border-purple-700/50',
    orange: 'bg-orange-50 border-orange-200 dark:bg-orange-900/30 dark:border-orange-700/50',
  };
  const valColors = {
    blue:   'text-blue-700 dark:text-blue-300',
    green:  'text-green-700 dark:text-green-300',
    yellow: 'text-yellow-700 dark:text-yellow-300',
    red:    'text-red-700 dark:text-red-300',
    purple: 'text-purple-700 dark:text-purple-300',
    orange: 'text-orange-700 dark:text-orange-300',
  };
  return (
    <div className={`rounded-xl border p-4 transition-colors ${styles[color]}`}>
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valColors[color]}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function Card({ title, icon, linkTo, linkText, children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700/50 shadow-sm p-5 transition-colors ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <span>{icon}</span>{title}
        </h3>
        {linkTo && (
          <Link to={linkTo} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
            {linkText || 'View'} &rarr;
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function ChainBadge({ chain }) {
  const colors = {
    solana:   'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
    base:     'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    ethereum: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
    bsc:      'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[chain] || 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'}`}>
      {chain?.toUpperCase()}
    </span>
  );
}

function BarChart({ items, maxVal }) {
  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.label} className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right shrink-0">{it.label}</span>
          <div className="flex-1 bg-gray-200 dark:bg-gray-700/50 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full rounded-full ${colors[i % colors.length]}`}
              style={{ width: `${maxVal ? (it.value / maxVal) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs text-gray-700 dark:text-gray-300 w-10 shrink-0">{fmt(it.value)}</span>
        </div>
      ))}
    </div>
  );
}

function DaemonDot({ running }) {
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${running ? 'bg-green-500 dark:bg-green-400 animate-pulse' : 'bg-red-400'}`} />
  );
}

/* ── main ────────────────────────────────────────────── */
export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [chainStats, setChainStats] = useState(null);
  const [recentTokens, setRecentTokens] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [mcDash, setMcDash] = useState(null);
  const [financeStats, setFinanceStats] = useState(null);
  const [marketIndex, setMarketIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, []);

  const fetchAll = async () => {
    try {
      const [ov, an, mi, rc, ch, recent, top, mc, fin, cmi] = await Promise.allSettled([
        api.get('/analytics/overview'),
        api.get('/analytics'),
        api.get('/analytics/milestones?limit=10'),
        api.get('/analytics/recovery?limit=10'),
        api.get('/analytics/tokens/chains'),
        api.get('/analytics/tokens/recent?limit=8'),
        api.get('/analytics/tokens/performance?range=7d'),
        api.get('/masterchef/dashboard'),
        api.get('/finance/stats'),
        api.get('/analytics/market-index'),
      ]);

      if (ov.status === 'fulfilled') setOverview(ov.value.data);
      if (an.status === 'fulfilled') setAnalytics(an.value.data);
      if (mi.status === 'fulfilled') setMilestones(mi.value.data);
      if (rc.status === 'fulfilled') setRecovery(rc.value.data);
      if (ch.status === 'fulfilled') setChainStats(ch.value.data);
      if (recent.status === 'fulfilled') setRecentTokens(recent.value.data);
      if (top.status === 'fulfilled') setTopPerformers(top.value.data);
      if (mc.status === 'fulfilled') setMcDash(mc.value.data);
      if (fin.status === 'fulfilled') setFinanceStats(fin.value.data);
      if (cmi.status === 'fulfilled') setMarketIndex(cmi.value.data);
      setError(null);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 dark:text-gray-400 text-lg animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-600 dark:text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  const totalAccounts = (overview?.users?.total || 0) + (overview?.groups?.total || 0);
  const paidAccounts = (overview?.users?.premium || 0) + (overview?.groups?.premium || 0);
  const newAccounts30d = (overview?.users?.new30d || 0) + (overview?.groups?.new30d || 0);

  const mcStats = mcDash?.stats;
  const mcProc = mcDash?.processes;
  const mcActivity = mcDash?.recentActivity || [];

  // M-Index: shared on-chain engine (marketIndexService) — same as Telegram `ck cmi`.
  const mIndex = (() => {
    const idx = marketIndex;
    if (!idx) return { score: 0, label: '—', color: 'red', desc: 'Loading…', rates: { ms24h: 0, recoveries24h: 0, scans24h: 0, drawdownBreadth: 0 } };

    const score = idx.score ?? 0;
    let color = 'red';
    if (score >= 80) color = 'green';
    else if (score >= 60) color = 'orange';
    else if (score >= 40) color = 'yellow';
    else if (score >= 20) color = 'yellow';

    return {
      score,
      label: idx.label || '—',
      color,
      desc: idx.desc || '',
      rates: {
        ms24h: idx.milestones ?? 0,
        recoveries24h: idx.recoveries ?? 0,
        scans24h: idx.scans ?? 0,
        drawdownBreadth: (idx.drawdownBreadth ?? 0) / 100,
      },
    };
  })();

  const chainItems = (chainStats?.chains || []).map((c) => ({
    label: c.chain?.toUpperCase() || '?',
    value: c.totalTokens,
  }));
  const chainMax = Math.max(...chainItems.map((c) => c.value), 1);

  const msItems = (milestones?.stats?.byMultiplier || []).slice(0, 6).map((m) => ({
    label: `${m._id}x`,
    value: m.count,
  }));
  const msMax = Math.max(...msItems.map((m) => m.value), 1);

  return (
    <div className="space-y-6">

      {/* ── Top stats row ───────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Tokens Tracked" value={fmt(overview?.tokens?.total)} sub={`${fmt(overview?.tokens?.active)} active`} color="purple" />
        <Stat label="Milestones" value={fmt(milestones?.total)} sub={`${fmt(milestones?.stats?.recent24h)} last 24h`} color="yellow" />
        <Stat label="Recovering" value={fmt(recovery?.stats?.recovering)} sub={`Avg ${fmtPct(recovery?.stats?.avgRecovery)}`} color="green" />
        <Stat label="Deep Drawdown" value={fmt(recovery?.stats?.deepDrawdown)} sub="80%+ from ATH" color="red" />
        <Stat label="Revenue 30d" value={fmtUsd(financeStats?.revenue30d ?? overview?.revenue?.last30Days)} sub={`${fmt(financeStats?.payments30d)} subscription payments`} color="orange" />

        {/* M-Index Sentiment */}
        <div className={`rounded-xl border p-4 transition-colors ${
          mIndex.color === 'green' ? 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700/50' :
          mIndex.color === 'orange' ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/30 dark:border-orange-700/50' :
          mIndex.color === 'yellow' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-700/50' :
          'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700/50'
        }`}>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">M-Index</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-2xl font-bold ${
              mIndex.color === 'green' ? 'text-green-700 dark:text-green-300' :
              mIndex.color === 'orange' ? 'text-orange-700 dark:text-orange-300' :
              mIndex.color === 'yellow' ? 'text-yellow-700 dark:text-yellow-300' :
              'text-red-700 dark:text-red-300'
            }`}>{mIndex.label}</span>
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
              mIndex.color === 'green' ? 'bg-green-100 text-green-700 dark:bg-green-800/40 dark:text-green-300' :
              mIndex.color === 'orange' ? 'bg-orange-100 text-orange-700 dark:bg-orange-800/40 dark:text-orange-300' :
              mIndex.color === 'yellow' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-800/40 dark:text-yellow-300' :
              'bg-red-100 text-red-700 dark:bg-red-800/40 dark:text-red-300'
            }`}>{mIndex.score}/100</span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{mIndex.desc}</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-mono">
            🎯{mIndex.rates.ms24h} · ♻️{mIndex.rates.recoveries24h} · 🔍{mIndex.rates.scans24h} · 📉{Math.round(mIndex.rates.drawdownBreadth * 100)}%
          </p>
        </div>
      </div>

      {/* ── Master Chef Strip ───────────────────────── */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-800 dark:to-gray-750 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <span>🍳</span> Master Chef
          </h3>
          <Link to="/trading/masterchef" className="text-xs text-blue-300 hover:text-blue-200">
            Open Dashboard &rarr;
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Active Positions</p>
            <p className="text-lg font-bold">{mcStats?.activePositions ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Total P&L</p>
            <p className={`text-lg font-bold ${(mcStats?.totalPnlPercent || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {mcStats?.totalPnlPercent != null ? `${mcStats.totalPnlPercent >= 0 ? '+' : ''}${mcStats.totalPnlPercent.toFixed(1)}%` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Win Rate</p>
            <p className="text-lg font-bold text-yellow-300">{mcStats?.winRate != null ? fmtPct(mcStats.winRate) : '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Today's Trades</p>
            <p className="text-lg font-bold">{mcStats?.todayTrades ?? '-'}</p>
          </div>
          <div className="flex items-center gap-2">
            <DaemonDot running={mcProc?.priceMonitor?.status === 'running'} />
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Price Monitor</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{mcProc?.priceMonitor?.intervalSeconds || '-'}s</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DaemonDot running={mcProc?.recoveryScanner?.status === 'running'} />
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Recovery Scanner</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{mcProc?.recoveryScanner?.intervalSeconds || '-'}s</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DaemonDot running={mcProc?.pumpScanner?.status === 'running'} />
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Pump Scanner</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{mcProc?.pumpScanner?.pendingSignals ?? '-'} pending</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main 3-Column Layout ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ─── Column 1: Token Analytics ─── */}
        <div className="space-y-5">
          <Card title="Recently Added" icon="🆕" linkTo="/tokens" linkText="All Tokens">
            <div className="space-y-2">
              {(recentTokens?.tokens || []).slice(0, 6).map((t, i) => (
                <div key={t.address || i} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{t.symbol}</span>
                    <ChainBadge chain={t.chain} />
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{ago(t.addedAt)} ago</span>
                </div>
              ))}
              {(!recentTokens?.tokens?.length) && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No recent tokens</p>
              )}
            </div>
          </Card>

          <Card title="Top Performers (7d)" icon="🏆" linkTo="/tokens" linkText="All Tokens">
            <div className="space-y-2">
              {(topPerformers?.tokens || []).slice(0, 6).map((t, i) => (
                <div key={t.address || i} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-gray-400 dark:text-gray-500 w-4">{i + 1}</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{t.symbol}</span>
                    <ChainBadge chain={t.chain} />
                  </div>
                  <span className="text-sm font-bold text-green-600 dark:text-green-400 shrink-0">
                    {t.athMultiplier ? `${t.athMultiplier.toFixed(1)}x` : '-'}
                  </span>
                </div>
              ))}
              {(!topPerformers?.tokens?.length) && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No data yet</p>
              )}
            </div>
          </Card>

          <Card title="Chain Distribution" icon="🔗" linkTo="/tokens">
            {chainItems.length > 0 ? (
              <BarChart items={chainItems} maxVal={chainMax} />
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No data</p>
            )}
          </Card>
        </div>

        {/* ─── Column 2: Milestones & Recovery ─── */}
        <div className="space-y-5">
          <Card title="Milestones" icon="🎯" linkTo="/milestones" linkText="View All">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-yellow-700 dark:text-yellow-300">{fmt(milestones?.total)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-red-700 dark:text-red-300">{fmt(milestones?.stats?.recent24h)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Last 24h</p>
              </div>
            </div>
            {msItems.length > 0 && <BarChart items={msItems} maxVal={msMax} />}
          </Card>

          <Card title="Recovery Tracking" icon="📈" linkTo="/recovery" linkText="View All">
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-green-700 dark:text-green-300">{fmt(recovery?.stats?.recovering)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovering</p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-orange-700 dark:text-orange-300">{fmt(recovery?.stats?.moderateDrawdown)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Moderate</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-red-700 dark:text-red-300">{fmt(recovery?.stats?.deepDrawdown)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Deep</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Total Tracked</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{fmt(recovery?.stats?.total)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Avg Recovery</span>
                <span className="text-green-600 dark:text-green-400 font-medium">{fmtPct(recovery?.stats?.avgRecovery)}</span>
              </div>
            </div>

            {recovery?.tokens?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wide">Recent Events</p>
                {recovery.tokens.slice(0, 4).map((t, i) => (
                  <div key={t.address || i} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700/30 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{t.symbol}</span>
                      <ChainBadge chain={t.chain} />
                    </div>
                    <span className={`text-xs font-medium ${t.recoveryPercent > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {t.recoveryPercent > 0 ? '+' : ''}{fmtPct(t.recoveryPercent)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Recent Activity" icon="📜" linkTo="/trading/masterchef/activity" linkText="View all">
            <div className="space-y-1.5">
              {mcActivity.slice(0, 6).map((a, i) => {
                const actionColors = {
                  BUY: 'text-green-600 dark:text-green-400',
                  SELL: 'text-red-600 dark:text-red-400',
                  T1: 'text-blue-600 dark:text-blue-400',
                  T2: 'text-blue-500 dark:text-blue-300',
                  T3: 'text-blue-400 dark:text-blue-200',
                  SL: 'text-red-700 dark:text-red-500',
                  TS: 'text-orange-600 dark:text-orange-400',
                };
                return (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700/30 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-xs font-bold w-8 ${actionColors[a.action] || 'text-gray-400'}`}>{a.action}</span>
                      <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{a.symbol}</span>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">{ago(a.timestamp)} ago</span>
                  </div>
                );
              })}
              {mcActivity.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </Card>
        </div>

        {/* ─── Column 3: Accounts, Finance, System ─── */}
        <div className="space-y-5">
          <Card title="Master Chef Trades" icon="🍳" linkTo="/trading/masterchef" linkText="View all">
            {/* Recovery Stats */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">RECOVERY</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-teal-700 dark:text-teal-300">{mcStats?.recovery?.active ?? 0}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Active</p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-teal-700 dark:text-teal-300">{mcStats?.recovery?.winRate ?? 0}%</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Win Rate</p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-2 text-center">
                  <p className={`text-lg font-bold ${(mcStats?.recovery?.totalPnlPercent || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {mcStats?.recovery?.totalPnlPercent != null ? `${mcStats.recovery.totalPnlPercent >= 0 ? '+' : ''}${mcStats.recovery.totalPnlPercent}%` : '-'}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">P&L</p>
                </div>
              </div>
              <div className="flex justify-between text-xs py-0.5">
                <span className="text-gray-500 dark:text-gray-400">Closed: {mcStats?.recovery?.closed ?? 0}</span>
                <span className="text-gray-500 dark:text-gray-400">Wins: {mcStats?.recovery?.wins ?? 0}</span>
              </div>
            </div>

            {/* Pump Stats */}
            <div className="border-t border-gray-100 dark:border-gray-700/50 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">PUMP</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{mcStats?.pump?.active ?? 0}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Active</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{mcStats?.pump?.winRate ?? 0}%</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Win Rate</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                  <p className={`text-lg font-bold ${(mcStats?.pump?.totalPnlPercent || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {mcStats?.pump?.totalPnlPercent != null ? `${mcStats.pump.totalPnlPercent >= 0 ? '+' : ''}${mcStats.pump.totalPnlPercent}%` : '-'}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">P&L</p>
                </div>
              </div>
              <div className="flex justify-between text-xs py-0.5">
                <span className="text-gray-500 dark:text-gray-400">Closed: {mcStats?.pump?.closed ?? 0}</span>
                <span className="text-gray-500 dark:text-gray-400">Wins: {mcStats?.pump?.wins ?? 0}</span>
              </div>
            </div>
          </Card>

          <Card title="Finance" icon="💰" linkTo="/subscriptions" linkText="Subscriptions">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{fmt(financeStats?.activePremium)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Active Premium</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{fmt(financeStats?.alpha)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Alpha</p>
              </div>
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wide">Subscription Activity</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Revenue (30d)</span>
                <span className="text-green-600 dark:text-green-400 font-medium">{fmtUsd(financeStats?.revenue30d)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Payments (30d)</span>
                <span className="text-blue-600 dark:text-blue-400 font-medium">{fmt(financeStats?.payments30d)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Pending payments</span>
                <span className="text-yellow-600 dark:text-yellow-400 font-medium">{fmt(financeStats?.pendingPayments)}</span>
              </div>
              <div className="flex justify-between text-sm py-1">
                <span className="text-gray-500 dark:text-gray-400">Expiring (7d)</span>
                <span className="text-gray-800 dark:text-white font-medium">{fmt(financeStats?.expiringSoon)}</span>
              </div>
            </div>
          </Card>

          <Card title="System" icon="⚙️" linkTo="/settings" linkText="Settings">
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Tokens Tracked (All Time)</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{fmt(analytics?.usage?.tokensTracked)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Alerts Sent</span>
                <span className="text-blue-600 dark:text-blue-400 font-medium">{fmt(analytics?.usage?.alertsSent)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-700/50">
                <span className="text-gray-500 dark:text-gray-400">Transactions</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{fmt(analytics?.transactions?.total)}</span>
              </div>
              <div className="flex justify-between text-sm py-1">
                <span className="text-gray-500 dark:text-gray-400">Free / Paid</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{fmt(analytics?.tiers?.free)} / {fmt(paidAccounts)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ── Quick Actions ───────────────────────────── */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
        {[
          { to: '/tokens', icon: '📋', label: 'Tokens' },
          { to: '/milestones', icon: '🎯', label: 'Milestones' },
          { to: '/recovery', icon: '📈', label: 'Recovery' },
          { to: '/trading/masterchef', icon: '🍳', label: 'Master Chef' },
          { to: '/users', icon: '👥', label: 'Users' },
          { to: '/subscriptions', icon: '⭐', label: 'Subscriptions' },
          { to: '/transactions', icon: '📝', label: 'Transactions' },
          { to: '/wallet-monitor', icon: '👛', label: 'Wallet Mon' },
          { to: '/frontrun', icon: '⚡', label: 'Frontrun' },
          { to: '/settings', icon: '⚙️', label: 'Settings' },
        ].map((q) => (
          <Link
            key={q.to}
            to={q.to}
            className="flex flex-col items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <span className="text-xl mb-1">{q.icon}</span>
            <span className="text-xs text-gray-600 dark:text-gray-300">{q.label}</span>
          </Link>
        ))}
      </div>

    </div>
  );
}
